import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout/legacy';
import { Card } from 'primereact/card';
import { Message } from 'primereact/message';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import layoutService from '../services/layoutService';

const ResponsiveGridLayout = WidthProvider(Responsive);

const BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
const COLS = { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 };
const DEFAULT_NEW_ITEM_SIZE = { w: 4, h: 4 };

const UnknownWidget = ({ type }) => (
  <Message
    severity="error"
    text={`No widget registered for type "${type}". Check that src/widgets/${type}/index.js exists.`}
    className="w-full"
  />
);

const generateId = () => `w_${Math.random().toString(36).slice(2, 10)}`;

/**
 * Editable dashboard grid — drag, resize, add, edit, and delete panels.
 * Reads and writes through layoutService (localStorage), so it shares
 * data live with <DashboardGridReadOnly /> — and any other open tab —
 * using the same `storageKey`.
 *
 * @param {Array} [fallbackLayout] - used only if nothing is saved yet
 *   under `storageKey`: { i, x, y, w, h, title, type, config }
 * @param {Object} widgetRegistry - map of `type` -> { Component, defaultConfig }
 * @param {string} [storageKey] - localStorage key to read/write (must
 *   match the key used by DashboardGridReadOnly to see these changes)
 * @param {Function} [onLayoutChange] - called with the full item array
 *   whenever it changes (drag, resize, add, edit, delete)
 */
const DashboardGridEditable = ({
  fallbackLayout = [],
  widgetRegistry = {},
  storageKey = layoutService.DEFAULT_STORAGE_KEY,
  onLayoutChange,
}) => {
  const [items, setItems] = useState(() => layoutService.getLayout(storageKey, fallbackLayout));

  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingId, setEditingId] = useState(null); // null => creating a new panel
  const [formTitle, setFormTitle] = useState('');
  const [formType, setFormType] = useState('');
  const [formConfigText, setFormConfigText] = useState('{}');
  const [formError, setFormError] = useState('');

  const typeOptions = useMemo(
    () => Object.keys(widgetRegistry).map((type) => ({ label: type, value: type })),
    [widgetRegistry]
  );

  // Re-read once if storageKey changes, and stay live-synced afterwards
  // (e.g. if another tab, or another instance of this grid, changes it).
  useEffect(() => {
    setItems(layoutService.getLayout(storageKey, fallbackLayout));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  useEffect(
    () => layoutService.subscribe(storageKey, setItems, fallbackLayout),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storageKey]
  );

  // Single choke point for every mutation: updates local state AND writes
  // through to layoutService. Because the subscription above receives the
  // exact same array reference back via the change event, React bails out
  // of a redundant re-render for our own writes — only genuinely external
  // changes (another tab/instance) actually cause a second update.
  const persist = useCallback(
    (nextItems) => {
      setItems(nextItems);
      layoutService.saveLayout(storageKey, nextItems);
      onLayoutChange?.(nextItems);
    },
    [storageKey, onLayoutChange]
  );

  const itemsById = useMemo(() => {
    const map = {};
    items.forEach((item) => {
      map[item.i] = item;
    });
    return map;
  }, [items]);

  const rglLayout = useMemo(
    () =>
      items.map(({ i, x, y, w, h, minW, maxW, minH, maxH, static: isStatic }) => ({
        i,
        x,
        y,
        w,
        h,
        ...(minW !== undefined && { minW }),
        ...(maxW !== undefined && { maxW }),
        ...(minH !== undefined && { minH }),
        ...(maxH !== undefined && { maxH }),
        ...(isStatic !== undefined && { static: isStatic }),
      })),
    [items]
  );

  const handleLayoutChange = useCallback(
    (updatedLayout) => {
      const merged = items.map((item) => {
        const positioned = updatedLayout.find((l) => l.i === item.i);
        return positioned
          ? { ...item, x: positioned.x, y: positioned.y, w: positioned.w, h: positioned.h }
          : item;
      });
      persist(merged);
    },
    [items, persist]
  );

  const openCreateDialog = () => {
    const defaultType = typeOptions[0]?.value || '';
    setEditingId(null);
    setFormTitle('');
    setFormType(defaultType);
    setFormConfigText(JSON.stringify(widgetRegistry[defaultType]?.defaultConfig || {}, null, 2));
    setFormError('');
    setDialogVisible(true);
  };

  const openEditDialog = (item) => {
    setEditingId(item.i);
    setFormTitle(item.title || '');
    setFormType(item.type);
    setFormConfigText(JSON.stringify(item.config || {}, null, 2));
    setFormError('');
    setDialogVisible(true);
  };

  const closeDialog = () => setDialogVisible(false);

  const handleTypeChange = (newType) => {
    setFormType(newType);
    // Only auto-fill defaults when creating a new panel, so switching type
    // while editing doesn't clobber config someone already customized.
    if (editingId === null) {
      setFormConfigText(JSON.stringify(widgetRegistry[newType]?.defaultConfig || {}, null, 2));
    }
  };

  const handleLoadDefaults = () => {
    setFormConfigText(JSON.stringify(widgetRegistry[formType]?.defaultConfig || {}, null, 2));
  };

  const handleSave = () => {
    if (!formType) {
      setFormError('Please select a widget type.');
      return;
    }

    let parsedConfig;
    try {
      parsedConfig = formConfigText.trim() ? JSON.parse(formConfigText) : {};
    } catch {
      setFormError('Config must be valid JSON.');
      return;
    }

    if (editingId) {
      persist(
        items.map((item) =>
          item.i === editingId
            ? { ...item, title: formTitle, type: formType, config: parsedConfig }
            : item
        )
      );
    } else {
      const maxY = items.reduce((max, item) => Math.max(max, item.y + item.h), 0);
      const newItem = {
        i: generateId(),
        x: 0,
        y: maxY,
        w: DEFAULT_NEW_ITEM_SIZE.w,
        h: DEFAULT_NEW_ITEM_SIZE.h,
        title: formTitle,
        type: formType,
        config: parsedConfig,
      };
      persist([...items, newItem]);
    }

    setDialogVisible(false);
  };

  const handleDelete = () => {
    if (!editingId) return;
    persist(items.filter((item) => item.i !== editingId));
    setDialogVisible(false);
  };

  return (
    <div>
      <div className="flex justify-content-end mb-3">
        <Button label="Add Widget" icon="pi pi-plus" onClick={openCreateDialog} />
      </div>

      <ResponsiveGridLayout
        className="layout"
        layouts={{ lg: rglLayout }}
        breakpoints={BREAKPOINTS}
        cols={COLS}
        rowHeight={80}
        margin={[16, 16]}
        draggableHandle=".widget-drag-handle"
        onLayoutChange={handleLayoutChange}
      >
        {rglLayout.map(({ i }) => {
          const item = itemsById[i];
          const widget = widgetRegistry[item.type];
          const Widget = widget?.Component || UnknownWidget;
          const config = { ...(widget?.defaultConfig || {}), ...(item.config || {}) };

          return (
            <div key={i}>
              <Card className="h-full" style={{ height: '100%', boxSizing: 'border-box' }}>
                <div className="flex align-items-center justify-content-between mb-2">
                  <div
                    className="widget-drag-handle flex align-items-center gap-2"
                    style={{ cursor: 'move', flex: 1, minWidth: 0 }}
                  >
                    <i className="pi pi-arrows-alt text-color-secondary" />
                    <span className="font-semibold text-overflow-ellipsis white-space-nowrap overflow-hidden">
                      {item.title || item.type}
                    </span>
                  </div>
                  <Button
                    icon="pi pi-pencil"
                    text
                    rounded
                    size="small"
                    onClick={() => openEditDialog(item)}
                    aria-label="Edit widget"
                  />
                </div>
                <Widget config={config} title={item.title} id={item.i} type={item.type} />
              </Card>
            </div>
          );
        })}
      </ResponsiveGridLayout>

      <Dialog
        header={editingId ? 'Edit Widget' : 'Add Widget'}
        visible={dialogVisible}
        onHide={closeDialog}
        style={{ width: '480px' }}
        footer={
          <div className="flex justify-content-between">
            <div>
              {editingId && (
                <Button label="Delete" icon="pi pi-trash" severity="danger" text onClick={handleDelete} />
              )}
            </div>
            <div className="flex gap-2">
              <Button label="Cancel" text onClick={closeDialog} />
              <Button label="Save" icon="pi pi-check" onClick={handleSave} />
            </div>
          </div>
        }
      >
        <div className="flex flex-column gap-3">
          {formError && <Message severity="error" text={formError} />}

          <div className="flex flex-column gap-2">
            <label htmlFor="widget-title">Title</label>
            <InputText
              id="widget-title"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              placeholder="Widget title"
              className="w-full"
              style={{ width: '100%' }}
            />
          </div>

          <div className="flex flex-column gap-2">
            <label htmlFor="widget-type">Type</label>
            <Dropdown
              id="widget-type"
              value={formType}
              options={typeOptions}
              onChange={(e) => handleTypeChange(e.value)}
              placeholder="Select a widget type"
              className="w-full"
              style={{ width: '100%' }}
              disabled={typeOptions.length === 0}
            />
            {typeOptions.length === 0 && (
              <small className="text-color-secondary">
                No widgets found in the registry — add one under src/widgets/&lt;type&gt;/.
              </small>
            )}
          </div>

          <div className="flex flex-column gap-2">
            <div className="flex align-items-center justify-content-between">
              <label htmlFor="widget-config">Config (JSON)</label>
              <Button label="Load Defaults" link size="small" onClick={handleLoadDefaults} />
            </div>
            <InputTextarea
              id="widget-config"
              value={formConfigText}
              onChange={(e) => setFormConfigText(e.target.value)}
              rows={8}
              autoResize
              className="w-full"
              style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.85rem' }}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default DashboardGridEditable;